import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
    Building2,
    Users,
    FileText,
    Receipt,
    Car,
    TrendingUp,
    TrendingDown,
    AlertCircle,
    CheckCircle2,
    Clock,
    ArrowRight,
    Plus,
    Euro,
    Calculator,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Dashboard = () => {
    const { api, user, isCommercial, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recentCompanies, setRecentCompanies] = useState([]);
    const [recentOpportunities, setRecentOpportunities] = useState([]);
    const [commissionSummary, setCommissionSummary] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const requests = [
                api.get('/dashboard/stats'),
                api.get('/companies?limit=5'),
                api.get('/opportunities?limit=5'),
            ];
            
            // Only fetch commission summary for commercials
            if (user?.role === 'commercial') {
                requests.push(api.get('/commissions/my-summary'));
            }
            
            const responses = await Promise.all(requests);
            setStats(responses[0].data);
            setRecentCompanies(responses[1].data.slice(0, 5));
            setRecentOpportunities(responses[2].data.slice(0, 5));
            
            if (user?.role === 'commercial' && responses[3]) {
                setCommissionSummary(responses[3].data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRoleTitle = () => {
        if (user?.role === 'super_admin') return 'Super Administrateur';
        if (user?.role === 'assistant') return 'Assistant Administratif';
        if (user?.role === 'commercial') return 'Commercial';
        if (user?.role === 'editorial') return 'Coordination Éditoriale';
        return 'Utilisateur';
    };

    const commercialStats = [
        {
            title: 'Prospects actifs',
            value: stats?.prospects_count || 0,
            icon: Building2,
            color: 'text-blue-500',
            bgColor: 'bg-blue-500/10',
            link: '/companies?status=prospect',
        },
        {
            title: 'Opportunités à suivre',
            value: stats?.opportunities_to_followup || 0,
            icon: TrendingUp,
            color: 'text-green-500',
            bgColor: 'bg-green-500/10',
            link: '/pipeline',
        },
        {
            title: 'Devis en attente',
            value: stats?.pending_quotes || 0,
            icon: FileText,
            color: 'text-orange-500',
            bgColor: 'bg-orange-500/10',
            link: '/quote-requests',
        },
        {
            title: 'Devis signés (mois)',
            value: stats?.signed_quotes_month || 0,
            icon: CheckCircle2,
            color: 'text-emerald-500',
            bgColor: 'bg-emerald-500/10',
            link: '/quote-requests?status=signed',
        },
        {
            title: 'Notes de frais en cours',
            value: stats?.pending_expenses || 0,
            icon: Receipt,
            color: 'text-purple-500',
            bgColor: 'bg-purple-500/10',
            link: '/expenses',
        },
    ];

    const adminStats = [
        {
            title: 'Demandes de devis à traiter',
            value: stats?.quote_requests_to_process || 0,
            icon: FileText,
            color: 'text-orange-500',
            bgColor: 'bg-orange-500/10',
            link: '/quote-requests?status=submitted',
            urgent: true,
        },
        {
            title: 'Notes de frais à valider',
            value: stats?.expenses_to_review || 0,
            icon: Receipt,
            color: 'text-purple-500',
            bgColor: 'bg-purple-500/10',
            link: '/expenses?status=submitted',
            urgent: true,
        },
        {
            title: 'Total prospects',
            value: stats?.prospects_count || 0,
            icon: Building2,
            color: 'text-blue-500',
            bgColor: 'bg-blue-500/10',
            link: '/companies',
        },
        {
            title: 'Opportunités actives',
            value: stats?.opportunities_to_followup || 0,
            icon: TrendingUp,
            color: 'text-green-500',
            bgColor: 'bg-green-500/10',
            link: '/pipeline',
        },
    ];

    const displayStats = isAdmin() ? adminStats : commercialStats;

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground" data-testid="dashboard-greeting">
                        Bonjour, {user?.first_name} !
                    </h1>
                    <p className="text-muted-foreground mt-2 text-sm md:text-base">
                        {getRoleTitle()} • {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/pipeline?new=true')}
                        className="rounded-full border-border/40 hover:border-primary/50"
                        data-testid="new-opportunity-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle opportunité
                    </Button>
                    <Button
                        onClick={() => navigate('/companies/new')}
                        className="rounded-full"
                        data-testid="new-company-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle entreprise
                    </Button>
                </div>
            </div>

            {/* Stats Grid - Bento Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger">
                {displayStats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <Link
                            key={stat.title}
                            to={stat.link}
                            data-testid={`stat-card-${index}`}
                        >
                            <Card className={`card-hover cursor-pointer h-full ${stat.urgent && stat.value > 0 ? 'border-orange-500/50' : ''}`}>
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground mb-2">{stat.title}</p>
                                            <p className="text-3xl font-bold font-heading text-foreground">{stat.value}</p>
                                        </div>
                                        <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                                            <Icon className={`w-6 h-6 ${stat.color}`} />
                                        </div>
                                    </div>
                                    {stat.urgent && stat.value > 0 && (
                                        <Badge variant="outline" className="mt-4 text-orange-500 border-orange-500/50 bg-orange-500/5">
                                            <AlertCircle className="w-3 h-3 mr-1" />
                                            À traiter
                                        </Badge>
                                    )}
                                </CardContent>
                            </Card>
                        </Link>
                    );
                })}
            </div>

            {/* Commission Widget for Commercials */}
            {user?.role === 'commercial' && commissionSummary && (
                <Card className="border border-green-500/20 bg-gradient-to-br from-green-500/5 to-emerald-500/5" data-testid="commission-widget">
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <div>
                            <CardTitle className="text-lg font-heading flex items-center gap-2 text-foreground">
                                <Calculator className="w-5 h-5 text-green-500" />
                                Mes commissions - {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                            </CardTitle>
                            <CardDescription className="mt-1">
                                {commissionSummary.has_variable_compensation === false 
                                    ? "Pas de rémunération variable" 
                                    : "Votre progression ce mois-ci"
                                }
                            </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" className="rounded-full border-border/40" asChild>
                            <Link to="/commissions" data-testid="view-commissions">
                                Détails
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Link>
                        </Button>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {commissionSummary.has_variable_compensation === false ? (
                            <div className="text-center py-6 text-muted-foreground">
                                <p>Votre contrat ne prévoit pas de part variable.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {/* CA HT Encaissé */}
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">CA HT Encaissé</p>
                                    <p className="text-2xl font-bold text-foreground">
                                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(commissionSummary.total_ca_ht || 0)}
                                    </p>
                                    {commissionSummary.previous_month && (
                                        <div className="flex items-center gap-1 text-sm">
                                            {(commissionSummary.total_ca_ht || 0) >= (commissionSummary.previous_month.total_ca_ht || 0) ? (
                                                <TrendingUp className="w-4 h-4 text-green-500" />
                                            ) : (
                                                <TrendingDown className="w-4 h-4 text-red-500" />
                                            )}
                                            <span className="text-muted-foreground">
                                                vs {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(commissionSummary.previous_month.total_ca_ht || 0)} le mois dernier
                                            </span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Commission estimée */}
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">Commission estimée</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(commissionSummary.commission_amount || 0)}
                                    </p>
                                    {commissionSummary.has_custom_brackets && (
                                        <Badge variant="outline" className="text-purple-500 border-purple-500/50 bg-purple-500/5">
                                            Barème personnalisé
                                        </Badge>
                                    )}
                                </div>
                                
                                {/* Taux effectif */}
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">Taux effectif</p>
                                    <p className="text-2xl font-bold text-foreground">
                                        {commissionSummary.rate_effective || 0}%
                                    </p>
                                    <Progress 
                                        value={Math.min((commissionSummary.rate_effective || 0) / 25 * 100, 100)} 
                                        className="h-2"
                                    />
                                    <p className="text-xs text-muted-foreground">Max: 25%</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Recent Activity Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Companies */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <div>
                            <CardTitle className="text-lg font-heading text-foreground">Entreprises récentes</CardTitle>
                            <CardDescription className="mt-1">Dernières entreprises ajoutées</CardDescription>
                        </div>
                        <Button variant="ghost" size="sm" className="rounded-full hover:bg-muted" asChild>
                            <Link to="/companies" data-testid="view-all-companies">
                                Voir tout
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Link>
                        </Button>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {recentCompanies.length === 0 ? (
                            <div className="text-center py-10 text-muted-foreground">
                                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-40" />
                                <p>Aucune entreprise</p>
                                <Button
                                    variant="link"
                                    className="mt-2 text-primary"
                                    onClick={() => navigate('/companies/new')}
                                >
                                    Ajouter une entreprise
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {recentCompanies.map((company) => (
                                    <Link
                                        key={company.id}
                                        to={`/companies/${company.id}`}
                                        className="flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors duration-150 border border-transparent hover:border-border/40"
                                        data-testid={`company-item-${company.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Building2 className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground">{company.legal_name}</p>
                                                <p className="text-sm text-muted-foreground">{company.city || 'Ville non renseignée'}</p>
                                            </div>
                                        </div>
                                        <Badge variant={company.status === 'client' ? 'default' : 'secondary'} className="font-medium">
                                            {company.status === 'client' ? 'Client' : 'Prospect'}
                                        </Badge>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Opportunities */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <div>
                            <CardTitle className="text-lg font-heading text-foreground">Opportunités en cours</CardTitle>
                            <CardDescription className="mt-1">Vos affaires actives</CardDescription>
                        </div>
                        <Button variant="ghost" size="sm" className="rounded-full hover:bg-muted" asChild>
                            <Link to="/pipeline" data-testid="view-pipeline">
                                Voir pipeline
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Link>
                        </Button>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {recentOpportunities.length === 0 ? (
                            <div className="text-center py-10 text-muted-foreground">
                                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-40" />
                                <p>Aucune opportunité</p>
                                <Button
                                    variant="link"
                                    className="mt-2 text-primary"
                                    onClick={() => navigate('/pipeline')}
                                >
                                    Créer une opportunité
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {recentOpportunities.map((opp) => (
                                    <Link
                                        key={opp.id}
                                        to={`/pipeline?opportunity=${opp.id}`}
                                        className="flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors duration-150 border border-transparent hover:border-border/40"
                                        data-testid={`opportunity-item-${opp.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate text-foreground">{opp.title}</p>
                                            <p className="text-sm text-muted-foreground truncate">
                                                {opp.company_name}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3 ml-4">
                                            {opp.estimated_amount && (
                                                <span className="text-sm font-semibold text-foreground">
                                                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(opp.estimated_amount)}
                                                </span>
                                            )}
                                            <Badge
                                                style={{ backgroundColor: opp.stage_color || '#6B7280' }}
                                                className="text-white font-medium"
                                            >
                                                {opp.stage_label}
                                            </Badge>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;
